import React, { useMemo, useState } from 'react';
import { PropsWithChildren } from 'react';
import { TooltipWithoutContainer } from '@instinet-analytics/core-lib';
import * as d3 from 'd3';
import { TooltipText } from 'utils/components/Tooltip/Tooltip';
import classNames from 'classnames';

import './DoughnutChart.scss';

export interface DoughnutChartSeriesItem {
    id: string;
    value: number;
    segmentWidth: number;
    classname: string;
    tooltip?: TooltipText;
}

type EnrichedItem = DoughnutChartSeriesItem & { ordinal: number };

type DoughnutChartProps = PropsWithChildren<{
    doughnutSize: number;
    className?: string;
    data: DoughnutChartSeriesItem[];
}>;

export function DoughnutChart(props: DoughnutChartProps) {
    const { className, data, doughnutSize, children } = props;

    const radius = doughnutSize / 2 - 1;

    const pie = useMemo(() => {
        const enrichedItems: EnrichedItem[] = data.map((d, i) => ({
            ...d,
            ordinal: i,
        }));
        const pieGenerator = d3
            .pie<unknown, EnrichedItem>()
            .value((d) => d.value);
        pieGenerator.sort((a, b) => (a.ordinal > b.ordinal ? 1 : -1));
        return pieGenerator(enrichedItems);
    }, [data]);

    const arcs = useMemo(() => {
        const arcPathGenerator = d3.arc();
        return pie.map((p) =>
            arcPathGenerator({
                innerRadius: radius - p.data.segmentWidth,
                outerRadius: radius,
                startAngle: p.startAngle,
                endAngle: p.endAngle,
            })
        );
    }, [radius, pie]);

    const [tooltipText, setTooltipText] = useState<TooltipText>();

    const cssClasses = classNames('DoughnutChart', className);

    return (
        <div className={cssClasses}>
            <TooltipWithoutContainer
                text={tooltipText}
                tooltipVisible={Boolean(tooltipText)}
                followCursor={true}
            >
                <svg
                    width={doughnutSize}
                    height={doughnutSize}
                    style={{ display: 'inline-block' }}
                >
                    <g
                        transform={`translate(${doughnutSize / 2}, ${
                            doughnutSize / 2
                        })`}
                    >
                        {arcs.map((arc, i) => {
                            const item = data[i];
                            return (
                                <path
                                    key={i}
                                    d={arc!}
                                    className={
                                        'doughnut-segment ' + item.classname
                                    }
                                    onMouseOver={() =>
                                        setTooltipText(item.tooltip)
                                    }
                                    onMouseLeave={() =>
                                        setTooltipText(undefined)
                                    }
                                />
                            );
                        })}
                    </g>
                </svg>
            </TooltipWithoutContainer>
            <div className="doughnut-centre-label">{children}</div>
        </div>
    );
}
